"use strict";
// THE ONE READING OF A TYPED DATE AND TIME (`RPM-021` criteria 7 to 9, `J-571`).
//
// The server places what this reads in the building's zone (`apps/server/src/modules/rpm/time/
// resolve.ts`), and Enter vitals stores nothing this does not read. One function on both sides, so a
// time the form accepts is never one the server calls unreadable — which is what left the owner's
// entry of 2026-10-02 unplaced: the form stored "9" during a pause, before "am" was typed.
//
// PURE: no zone, no clock. Whether the wall time exists in a zone on that day is the server's question.
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseLocalDate = parseLocalDate;
exports.parseLocalTime = parseLocalTime;
exports.timeQuestion = timeQuestion;
const MONTHS = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
];
function monthOf(word) {
    const w = word.toLowerCase().replace(/\.$/, "");
    const i = MONTHS.findIndex((m) => m === w || (w.length >= 3 && m.startsWith(w)));
    return i === -1 ? null : i + 1;
}
const collapse = (text) => text.trim().replace(/\s+/g, " ");
function realDate(year, month, day) {
    if (month < 1 || month > 12 || day < 1)
        return null;
    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return day > last ? null : { year, month, day };
}
const fullYear = (y) => (y.length === 2 ? 2000 + Number(y) : Number(y));
/** Criterion 7's forms: ISO, month-first numerals with `/`, `-` or `.`, and the month by name. */
function parseLocalDate(text) {
    const t = collapse(text);
    let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
    if (m)
        return realDate(Number(m[1]), Number(m[2]), Number(m[3]));
    m = /^(\d{1,2})([/.-])(\d{1,2})\2(\d{2}|\d{4})$/.exec(t);
    if (m)
        return realDate(fullYear(m[4] ?? ""), Number(m[1]), Number(m[3]));
    m = /^(\d{1,2}) ([A-Za-z]+\.?),? (\d{4})$/.exec(t);
    if (m) {
        const month = monthOf(m[2] ?? "");
        return month === null ? null : realDate(Number(m[3]), month, Number(m[1]));
    }
    m = /^([A-Za-z]+\.?) (\d{1,2}),? (\d{4})$/.exec(t);
    if (m) {
        const month = monthOf(m[1] ?? "");
        return month === null ? null : realDate(Number(m[3]), month, Number(m[2]));
    }
    return null;
}
/**
 * The time part, normalised in the block's order: H.MM becomes H:MM, remaining dots are stripped,
 * whitespace collapses, a trailing `a`, `am`, `p` or `pm` becomes ` AM` or ` PM`, and a compact
 * `930a` or `0930` gains its colon. The dot rules are the TIME part's: applied to a date they would
 * turn 07.15.2026 into 07152026, which criterion 7 accepts as a date (`D-996`).
 */
function normaliseTime(text) {
    const t = collapse(text
        .replace(/(\d{1,2})\.(\d{2})(?!\d)/, "$1:$2")
        .replace(/\./g, "")
        .replace(/(\d)\s*([ap])\s*m?\s*$/i, (_x, d, ap) => `${d} ${ap.toUpperCase()}M`));
    // `930 AM`: hours and minutes run together before AM or PM.
    const withMeridiem = /^(\d{1,2})(\d{2}) ([AP]M)$/.exec(t);
    if (withMeridiem)
        return `${withMeridiem[1] ?? ""}:${withMeridiem[2] ?? ""} ${withMeridiem[3] ?? ""}`;
    // `0930`: four digits on the 24-hour clock.
    const fourDigits = /^(\d{2})(\d{2})$/.exec(t);
    if (fourDigits)
        return `${fourDigits[1] ?? ""}:${fourDigits[2] ?? ""}`;
    return t;
}
/**
 * Criteria 8 and 9, and the owner's forms of 2026-10-02 (`J-571`): HH:MM and HH:MM:SS (seconds read
 * and dropped), either with AM or PM; a bare hour with AM or PM (`9am`, `9a`); hours and minutes run
 * together with AM or PM (`930a`); and four digits on the 24-hour clock (`0930`). A bare hour, or
 * three digits, with neither AM nor PM is refused: `9` is morning or evening, and the form asks.
 */
function parseLocalTime(text) {
    const raw = collapse(text);
    if (/^\d{3}$/.test(raw))
        return null; // `930` is 9:30 in the morning or the evening
    const t = normaliseTime(raw);
    const m = /^(\d{1,2})(?::(\d{2})(?::(\d{2}))?)?(?: (AM|PM))?$/.exec(t);
    if (!m)
        return null;
    const meridiem = m[4];
    if (m[2] === undefined && meridiem === undefined)
        return null; // a bare "11" says nothing
    let hour = Number(m[1]);
    const minute = m[2] === undefined ? 0 : Number(m[2]);
    if (minute > 59 || (m[3] !== undefined && Number(m[3]) > 59))
        return null;
    if (meridiem) {
        if (hour < 1 || hour > 12)
            return null;
        hour = (hour % 12) + (meridiem === "PM" ? 12 : 0);
    }
    else if (hour > 23)
        return null;
    return { hour, minute };
}
/**
 * What the form says of a time it cannot read, or null when it reads it (or nothing is typed yet):
 * a bare hour, or hours and minutes, with no AM or PM is asked about; anything else is named.
 */
function timeQuestion(text) {
    const t = collapse(text);
    if (t === "" || parseLocalTime(t) !== null)
        return null;
    const bare = /^(\d{1,2})(\d{2})?$/.exec(t);
    if (bare && Number(bare[1]) >= 1 && Number(bare[1]) <= 12 && Number(bare[2] ?? "0") <= 59) {
        const said = bare[2] === undefined ? (bare[1] ?? "") : `${bare[1] ?? ""}:${bare[2]}`;
        return `${said} AM or ${said} PM?`;
    }
    return "Type the time like 9am, 9:30 PM or 21:30.";
}
//# sourceMappingURL=local-date-time.js.map